// src/components/PaymentPage.jsx
import { useState, useRef } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { notifyAllDoctors } from "../hooks/useNotifications";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Upload, CheckCircle, Clock, Copy, CheckCheck } from "lucide-react";
// Cloudinary - no import needed
const CLOUDINARY_CLOUD = "dq81k0qo3";
const CLOUDINARY_PRESET = "nodom_receipts";

const BANK_DETAILS = {
  bankName: "بريد الجزائــر",
  RIP: "00799999002918023173",
  CCP: "0029180231-73",
  accountName: "سويد حفصة",
  amount: " 1000 دج / شهرياً"
};

export default function PaymentPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { t, lang } = useLanguage();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null); // "rip" | "ccp" | null
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  // Already pending or active
  if (profile?.status === "active" && profile?.subscription === "paid") {
    return (
      <div className="page-content fade-in">
        <div className="container-sm">
          <div className="card card-lg text-center">
            <CheckCircle size={52} style={{ color: "var(--color-forest)", margin: "0 auto 1rem" }} />
            <h2>{lang === "ar" ? "اشتراكك نشط!" : "Your subscription is active!"}</h2>
            <p>{lang === "ar" ? "يمكنك الآن الوصول إلى المحادثة مع طبيبك." : "You can now access your doctor chat."}</p>
            <a href="/consult/paid" className="btn btn-primary btn-lg" style={{ marginTop: "1.5rem" }}>
              {t("paidConsult")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (profile?.subscription === "paid" && profile?.status === "pending") {
    return (
      <div className="page-content fade-in">
        <div className="container-sm">
          <div className="card card-lg text-center">
            <Clock size={52} style={{ color: "var(--color-warning)", margin: "0 auto 1rem" }} />
            <h2>{t("pendingStatus")}</h2>
            <p>{t("pendingMsg")}</p>
            <div className="alert alert-warn" style={{ marginTop: "1.5rem" }}>
              {lang === "ar" ? "سيتواصل معك الطبيب بعد الموافقة على الإيصال خلال 24 ساعة" : "The doctor will contact you after receipt approval within 24 hours"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function copyText(text, key) {
  navigator.clipboard.writeText(text);
  setCopied(key);
  setTimeout(() => setCopied(null), 1000);
}

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && (dropped.type.startsWith("image/") || dropped.type === "application/pdf")) {
      setFile(dropped);
    }
  }

  async function handleUpload() {
  if (!file || !user) return;
  setUploading(true);
  setError("");
  setProgress(0);

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_PRESET);
    formData.append("folder", "nodom_receipts");

    const isPDF = file.type === "application/pdf";
    const resourceType = file.type.startsWith("video/") ? "video" : "image";

    // Simulate progress while uploading
    const progressInterval = setInterval(() => {
      setProgress(prev => prev < 85 ? prev + 10 : prev);
    }, 300);

    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/${resourceType}/upload`,
      { method: "POST", body: formData }
    );

    clearInterval(progressInterval);

    if (!res.ok) throw new Error("Upload failed");

    const data = await res.json();
    // تأكد أن PDF يحتوي على امتداد .pdf في الـ URL
    let url = data.secure_url;
    if (isPDF && !url.endsWith(".pdf")) {
      url = url.replace(/\.[^/.]+$/, "") + ".pdf";
    }

    setProgress(100);

    // Save to Firestore (same as before)
    await setDoc(doc(db, "receipts", user.uid), {
      url,
      fileName: file.name,
      timestamp: serverTimestamp(),
      uid: user.uid,
      userName: profile?.name
    });
    await setDoc(doc(db, "users", user.uid), {
      subscription: "paid",
      status: "pending",
      receiptUrl: url,
      receiptUploadedAt: serverTimestamp()
    }, { merge: true });

          // إشعار جميع الأطباء
      await notifyAllDoctors(
        `مشترك جديد بانتظار الموافقة: ${profile?.name || user.email}`,
        "receipt"
      );
      await refreshProfile();
      setUploaded(true);

  } catch (err) {
    setError(lang === "ar" ? "حدث خطأ أثناء الرفع، حاول مجدداً" : "Upload failed, please try again");
  }

  setUploading(false);
}

  return (
    <div className="page-content fade-in">
      <div className="container-sm">
        <div className="text-center" style={{ marginBottom: "2rem" }}>
          <h2>{t("paymentTitle")}</h2>
          <p>{lang === "ar" ? "اشترك في الخدمة المدفوعة للحصول على استشارة طبية متخصصة" : "Subscribe to the paid service for specialized medical consultation"}</p>
        </div>

        {/* Benefits */}
        <div className="card" style={{ marginBottom: "1.5rem", background: "var(--color-forest-lt)" }}>
          <h4 style={{ marginBottom: "1rem", color: "var(--color-forest-dk)" }}>
            {lang === "ar" ? "مميزات الاشتراك" : "Subscription Benefits"}
          </h4>
          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {(lang === "ar" ? [
              "محادثة مباشرة مع طبيب متخصص في ADHD",
              "خطة تمارين يومية مخصصة لطفلك",
              "تقارير تقدم شهرية",
              "دعم مستمر على مدار الأسبوع",
              "الوصول لمكتبة الموارد التعليمية"
            ] : [
              "Direct chat with an ADHD specialist",
              "Personalized daily exercises for your child",
              "Monthly progress reports",
              "Ongoing weekly support",
              "Access to educational resource library"
            ]).map((item, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.925rem" }}>
                <CheckCircle size={16} style={{ color: "var(--color-forest)", flexShrink: 0 }} />
                {item}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: "1rem", padding: "0.75rem", background: "var(--color-white)", borderRadius: "var(--radius-md)", display: "inline-block" }}>
            <span style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--color-forest)" }}>1000</span>
            <span style={{ color: "var(--color-gray-500)", fontSize: "0.9rem" }}> {lang === "ar" ? "1000 دج / شهريا" : "SAR / month"}</span>
          </div>
        </div>

        {/* Bank Details */}
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <h4 style={{ marginBottom: "1rem" }}>{lang === "ar" ? "بيانات التحويل البنكي" : "Bank Transfer Details"}</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <InfoRow label={lang === "ar" ? "البنك" : "Bank"} value={BANK_DETAILS.bankName} />
            <InfoRow label={lang === "ar" ? "اسم الحساب" : "Account Name"} value={BANK_DETAILS.accountName} />
            {/* RIP */}
<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0.8rem", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-gray-200)" }}>
  <div>
    <div style={{ fontSize: "0.78rem", color: "var(--color-gray-400)", marginBottom: "0.2rem" }}>RIP</div>
    <div style={{ fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.05em", fontSize: "0.95rem" }}>
      {BANK_DETAILS.RIP}
    </div>
  </div>
  <button
    onClick={() => copyText(BANK_DETAILS.RIP, "rip")}
    className="btn btn-sm"
    title={lang === "ar" ? "نسخ" : "Copy"}
    style={{
      background: copied === "rip" ? "var(--color-forest)" : "var(--color-forest-lt)",
      color: copied === "rip" ? "#fff" : "var(--color-forest)",
      border: "1.5px solid var(--color-forest)",
      transition: "all 0.3s"
    }}
  >
    {copied === "rip" ? <CheckCheck size={16} /> : <Copy size={16} />}
  </button>
</div>

{/* CCP */}
<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0.8rem", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-gray-200)" }}>
  <div>
    <div style={{ fontSize: "0.78rem", color: "var(--color-gray-400)", marginBottom: "0.2rem" }}>CCP</div>
    <div style={{ fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.05em", fontSize: "0.95rem" }}>
      {BANK_DETAILS.CCP}
    </div>
  </div>
  <button
    onClick={() => copyText(BANK_DETAILS.CCP, "ccp")}
    className="btn btn-sm"
    title={lang === "ar" ? "نسخ" : "Copy"}
    style={{
      background: copied === "ccp" ? "var(--color-forest)" : "var(--color-forest-lt)",
      color: copied === "ccp" ? "#fff" : "var(--color-forest)",
      border: "1.5px solid var(--color-forest)",
      transition: "all 0.3s"
    }}
  >
    {copied === "ccp" ? <CheckCheck size={16} /> : <Copy size={16} />}
  </button>
</div>



            <InfoRow label={lang === "ar" ? "المبلغ" : "Amount"} value={BANK_DETAILS.amount} />
          </div>
        </div>

        {/* Upload */}
        <div className="card">
          <h4 style={{ marginBottom: "1rem" }}>{t("uploadReceipt")}</h4>

          {!uploaded ? (
            <>
              <div
                className={`upload-zone ${dragOver ? "drag-over" : ""}`}
                style={{ position: "relative" }}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={e => setFile(e.target.files[0])}
                  style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                />
                <Upload size={32} style={{ color: "var(--color-gray-300)", margin: "0 auto 0.75rem", display: "block" }} />
                <p style={{ marginBottom: "0.25rem", fontWeight: 600 }}>
                  {file ? file.name : t("uploadHint")}
                </p>
                <p className="text-muted text-sm">
                  {lang === "ar" ? "صورة JPG/PNG — حتى 5 ميغا" : "JPG/PNG/PDF — up to 5MB"}
                </p>
              </div>

              {file && (
                <div style={{ marginTop: "0.75rem" }} className="flex items-center gap-2">
                  <CheckCircle size={16} style={{ color: "var(--color-forest)" }} />
                  <span style={{ fontSize: "0.875rem" }}>{file.name}</span>
                </div>
              )}

              {uploading && (
                <div style={{ marginTop: "1rem" }}>
                  <div className="progress-bar-wrap">
                    <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-sm text-muted" style={{ marginTop: "0.3rem" }}>{progress}%</p>
                </div>
              )}

              {error && <div className="alert alert-error" style={{ marginTop: "0.75rem" }}>{error}</div>}

              <button
                className="btn btn-primary btn-full"
                style={{ marginTop: "1.25rem" }}
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? (
                  <><div className="spinner" style={{ width: 18, height: 18 }} /> {lang === "ar" ? "جار الرفع..." : "Uploading..."}</>
                ) : (
                  <><Upload size={16} /> {t("uploadReceipt")}</>
                )}
              </button>
            </>
          ) : (
            <div className="text-center">
              <CheckCircle size={48} style={{ color: "var(--color-forest)", margin: "0 auto 1rem" }} />
              <h4>{lang === "ar" ? "تم رفع الإيصال بنجاح!" : "Receipt uploaded successfully!"}</h4>
              <p className="text-muted">{t("pendingMsg")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid var(--color-gray-100)", gap: "1rem" }}>
      <span style={{ fontSize: "0.875rem", color: "var(--color-gray-400)" }}>{label}</span>
      <span style={{ fontSize: "0.925rem", fontWeight: 600 }}>{value}</span>
    </div>
  );
}
